import { injectable } from 'tsyringe';
import { Roles } from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { UsersAdminService, type ManagedUser, type RoleView } from './UsersAdminService';

interface InviteForm {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
}

const EMPTY_INVITE: InviteForm = { email: '', firstName: '', lastName: '', role: Roles.VIEWER };

@injectable()
export class UsersAdminPageViewModel extends BaseViewModel {
    users: ManagedUser[] | null = null;
    roles: RoleView[] | null = null;
    loading: boolean = true;

    inviteOpen: boolean = false;
    inviteForm: InviteForm = { ...EMPTY_INVITE };
    saving: boolean = false;

    constructor(private service: UsersAdminService) {
        super();
    }

    get roleOptions(): { value: string; label: string }[] {
        return this.roles?.map((r) => ({ value: r.name, label: r.name })) ?? [];
    }

    async load(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            const [users, roles] = await Promise.all([this.service.listUsers(), this.service.listRoles()]);
            this.users = users;
            this.roles = roles;
        } catch {
            this.users = null;
            this.roles = null;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    openInvite(): void {
        this.inviteForm = { ...EMPTY_INVITE };
        this.inviteOpen = true;
        this.notifyChange();
    }

    closeInvite(): void {
        this.inviteOpen = false;
        this.notifyChange();
    }

    setInviteField<K extends keyof InviteForm>(key: K, value: InviteForm[K]): void {
        this.inviteForm = { ...this.inviteForm, [key]: value };
        this.notifyChange();
    }

    async invite(tenantId: string | undefined): Promise<{ success: boolean; email: string; error?: string }> {
        this.saving = true;
        this.notifyChange();
        const { email, firstName, lastName, role } = this.inviteForm;
        try {
            await this.service.inviteUser({
                email,
                firstName,
                lastName,
                roles: [role],
                tenantId,
            });
            this.inviteForm = { ...EMPTY_INVITE };
            this.inviteOpen = false;
            void this.load();
            return { success: true, email };
        } catch (e) {
            return { success: false, email, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async updateRoles(userId: string, newRoles: string[]): Promise<{ success: boolean; roles: string[]; error?: string }> {
        const filtered = newRoles.filter(Boolean);
        if (filtered.length === 0) return { success: false, roles: [], error: 'At least one role required' };
        try {
            await this.service.setUserRoles(userId, filtered);
            void this.load();
            return { success: true, roles: filtered };
        } catch (e) {
            return { success: false, roles: filtered, error: (e as Error).message };
        }
    }

    async deleteUser(userId: string, email: string): Promise<{ success: boolean; email: string; error?: string }> {
        try {
            await this.service.deleteUser(userId);
            void this.load();
            return { success: true, email };
        } catch (e) {
            return { success: false, email, error: (e as Error).message };
        }
    }

}
